// Generated by Copilot- Start
import React from "react";
import DtvModal from "@directv-digital/dds.ui.dtv-modal";
import { modalLinkPosition } from "src/utils/link-click";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "../../video-lab/dtv-text";
import DtvDivider from "@directv-digital/dds.ui.dtv-divider";
import { ChannelCardV2, PackageSummary, SvodPromoMap } from "lib/helper/channel-interactive-tool-v2";
import { IChnlInteractiveThemeUI } from "lib/helper/channel-interactive-tool-theme";
import ModalChannelHeader from "./shared/ModalChannelHeader";
import ModalPackageSection from "./shared/ModalPackageSection";
import { buildModalPackageLists } from "../utils/modal-package-builder";
import { parseModalContent } from "../utils/modal-content-parser";
import { buildSvodFooterText } from "../utils/svod-footer-builder";
import { dtvModalStyle, dtvModalBodyStyle, dtvModalRootSx } from "../styles/component-styles";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";

interface ChannelModalV2Props {
  open: boolean;
  channel?: ChannelCardV2;
  onClose: () => void;
  theme?: IChnlInteractiveThemeUI;
  pkgModalContent?: any;
  packageSummaries?: Record<string, PackageSummary>;
  svodPromoMap?: SvodPromoMap;
  chnlSrc?: string;
  addonsLegalText?: string;
  displayGenrePack?: boolean;
  displayGenrePackSatellite?: boolean;
}

const ChannelModalV2: React.FC<ChannelModalV2Props> = ({
  open,
  channel,
  onClose,
  theme: _theme,
  pkgModalContent,
  packageSummaries = {},
  svodPromoMap = {},
  chnlSrc,
  addonsLegalText,
  displayGenrePack,
  displayGenrePackSatellite
}) => {
  try {
    if (!channel) {
      return null;
    }

    // Determine if this is satellite variant using chnlSrc (consistent with other modals)
    const isSatellite = chnlSrc === "dtvClup";

    // Determine if genre packages section should be enabled based on variant
    const isGenrePackagesEnabled = isSatellite ? Boolean(displayGenrePackSatellite) : Boolean(displayGenrePack);

    // Build package lists using centralized utility
    const { signaturePackageList, genrePackageList } = buildModalPackageLists("channel", {
      channel,
      packageSummaries,
      chnlSrc,
      svodPromoMap,
      isGenrePackagesEnabled
    });

    // Determine channel category for analytics
    let channelCategory = "channel";
    if (channel.categoryId) {
      channelCategory = channel.categoryId.toLowerCase();
    }

    // Parse CMS content using centralized helper with package counts for singular/plural
    const { signaturePkgTxt, genrePkgTxt, genrePacksLabel, alsoAvailableInTxt, availableAsAddonTxt, shopCta, genrePacksUrl, shopCtaAccessibility } = parseModalContent(
      pkgModalContent,
      isSatellite
    );

    // Build SVOD footer text for legal disclaimer
    const formattedSvodList = buildSvodFooterText(channel.label, svodPromoMap);
    const showFooter = addonsLegalText && formattedSvodList.length > 0;

    return (
      <DtvModal
        open={open}
        handleClose={onClose}
        // WAT-12797 (AC7): the close X reports the same modal_<category>_<name> as the CTAs inside.
        closeButtonProps={{ "data-linkposition": modalLinkPosition(`${channelCategory}_${channel.label}`) }}
        modalStyle={dtvModalStyle}
        modalBodyStyle={dtvModalBodyStyle}
        sx={dtvModalRootSx}
      >
        <DtvBox sx={{
 display: "flex",
flexDirection: "column",
p: "1px"
}}>
          {/* Channel Header with Logo */}
          <ModalChannelHeader logo={channel.logo} label={channel.label} isOvLogo={channel?.isOvLogo} />

          <DtvDivider sx={{
 width: "100% !important",
my: {
 xs: "18px",
md: "21px"
}
}} />

          {/* Signature Packages Section */}
          <ModalPackageSection
            id="package-mapping-with-channel"
            heading={signaturePkgTxt}
            packages={signaturePackageList}
            channelName={channel.label}
            channelCategory={channelCategory}
            topMargin="0px"
          />

          {/* Genre Packages Section */}
          <ModalPackageSection
            id="package-mapping-with-channel-genre"
            heading={genrePkgTxt}
            packages={genrePackageList}
            channelName={channel.label}
            channelCategory={channelCategory}
            showDivider={signaturePackageList.length > 0}
            topMargin="26px"
          />

          {/* Genre Packs Add-on Section (for channels eligible via SVOD addon mapping) */}
          {isGenrePackagesEnabled && channel.isGenreAddonChannel && (
            <ModalPackageSection
              heading={alsoAvailableInTxt}
              packages={[{
                id: "genre-packs-addon",
                displayLabel: genrePacksLabel,
                packageLegal: availableAsAddonTxt,
                ctaObj: {
 cta: shopCta,
ctaUrl: genrePacksUrl,
ctaAccessibility: shopCtaAccessibility
},
                type: "genre"
              }]}
              channelName={channel.label}
              channelCategory={channelCategory}
              showDivider={signaturePackageList.length > 0 || genrePackageList.length > 0}
              topMargin="26px"
            />
          )}

          {/* SVOD Add-ons Legal Footer */}
          {showFooter && (
            <>
              <DtvDivider sx={{
 width: "100%",
my: "16px"
}} />
              <DtvText
                variant="smalltext"
                content={addonsLegalText.replace("[month], [channelName]", formattedSvodList)}
                sx={{
                  fontSize: {
 xs: "12px !important",
sm: "14px !important"
}
                }}
              />
            </>
          )}
        </DtvBox>
      </DtvModal>
    );
  } catch (error) {
    console.error("Error in ChannelModalV2 >> ", error);
    return <ErrorDisplay error={error} component="Channel Modal V2" />;
  }
};

export default React.memo(ChannelModalV2);
